import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import apiEndpoints from "../../apiConfig";

const PersonalDetails = ({ form, setField, isMobile, showErrors }) => {
  const [options, setOptions] = useState({
    bloodGroups: [],
    education: [],
    countries: [],
    states: [],
    cities: [],
    maritalStatuses: [],
    profession: [],
    religions: [],
    castes: [],
    zodiacSigns: [],
    zodiacStars: [],
    annualIncome: [],
  });

  const fetchFieldOptions = async (key, table, parentTable, parentId) => {
    try {
      let url = `${apiEndpoints.getOptions}?table=${table}`;
      if (parentTable && parentId) {
        url += `&parent_table=${parentTable}&parent_id=${parentId}`;
      }

      const res = await fetch(url);
      const data = await res.json();

      if (Array.isArray(data)) {
        setOptions((prev) => ({ ...prev, [key]: data }));
      } else {
        console.error("API returned unexpected data:", data);
        setOptions((prev) => ({ ...prev, [key]: [] }));
      }
    } catch (err) {
      console.error("Failed to fetch options for", table, err);
      setOptions((prev) => ({ ...prev, [key]: [] }));
    }
  };

  // ✅ Load static lists once on mount
  useEffect(() => {
    fetchFieldOptions("education", "education");
    fetchFieldOptions("bloodGroups", "blood_group");
    fetchFieldOptions("countries", "country");
    fetchFieldOptions("maritalStatuses", "marital_status");
    fetchFieldOptions("profession", "profession");
    fetchFieldOptions("annualIncome", "annual_income");
    fetchFieldOptions("religions", "religion");
    fetchFieldOptions("castes", "caste");
    fetchFieldOptions("zodiacSigns", "zodiac_moonsign");
    fetchFieldOptions("zodiacStars", "zodiac_star");
    // Load ALL states
    fetchFieldOptions("states", "state");
  }, []);

  // Removed country-dependent state fetch

  // ✅ Fetch cities whenever state changes
  useEffect(() => {
    if (form.state_id) {
      fetchFieldOptions("cities", "city", "state", form.state_id);
    } else {
      setOptions((prev) => ({ ...prev, cities: [] }));
    }
  }, [form.state_id]);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Personal Details
      </Typography>
      <Grid container spacing={2}>
        {/* Name */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <TextField
            fullWidth
            label="Name"
            value={form.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            error={showErrors && !form.name}
            helperText={showErrors && !form.name ? "Name is required" : ""}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <TextField
            fullWidth
            label="Email"
            value={form.email || ""}
            onChange={(e) => setField("email", e.target.value)}
            error={showErrors && !form.email}
            helperText={showErrors && !form.email ? "Email is required" : ""}
          />
        </Grid>

        {/* Age */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <TextField
            fullWidth
            label="Age"
            type="number"
            value={form.age || ""}
            onChange={(e) => setField("age", e.target.value)}
            inputProps={{ min: 0, max: 120 }}
            error={
              showErrors &&
              (!form.age || Number(form.age) < 18 || Number(form.age) > 42)
            }
            helperText={
              showErrors &&
              (!form.age || Number(form.age) < 18 || Number(form.age) > 42)
                ? "Age must be between 18 and 42"
                : ""
            }
          />
        </Grid>

        {/* Gender */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <TextField
            select
            fullWidth
            label="Gender"
            value={form.gender || ""}
            onChange={(e) => setField("gender", e.target.value)}
            error={showErrors && !form.gender}
            helperText={showErrors && !form.gender ? "Gender is required" : ""}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
        </Grid>

        {/* DOB */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <TextField
            fullWidth
            label="Date of Birth"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.dob || ""}
            onChange={(e) => setField("dob", e.target.value)}
            error={showErrors && !form.dob}
            helperText={showErrors && !form.dob ? "DOB is required" : ""}
          />
        </Grid>

        {/* Birth Time */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <TextField
            fullWidth
            label="Birth Time"
            type="time"
            InputLabelProps={{ shrink: true }}
            value={form.birth_time || ""}
            onChange={(e) => setField("birth_time", e.target.value)}
            error={showErrors && !form.birth_time}
            helperText={
              showErrors && !form.birth_time ? "Birth time is required" : ""
            }
          />
        </Grid>

        {/* Birth Place */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <TextField
            fullWidth
            label="Birth Place"
            value={form.birth_place || ""}
            onChange={(e) => setField("birth_place", e.target.value)}
            error={showErrors && !form.birth_place}
            helperText={
              showErrors && !form.birth_place ? "Birth Place is required" : ""
            }
          />
        </Grid>

        {/* Education */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.education}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.education.find((c) => c.name === form.education) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("education", v.name); // store string
                setField("education_id", v.id); // also keep id
              } else {
                setField("education", "");
                setField("education_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Education"
                fullWidth
                error={showErrors && !form.education}
                helperText={
                  showErrors && !form.education ? "Education is required" : ""
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <TextField
            fullWidth
            label="Address"
            value={form.address || ""}
            onChange={(e) => setField("address", e.target.value)}
            error={showErrors && !form.address}
            helperText={
              showErrors && !form.address ? "Address is required" : ""
            }
          />
        </Grid>

        {/* Blood Group */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.bloodGroups}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.bloodGroups.find((c) => c.name === form.blood_group) ||
              null
            }
            onChange={(_, v) => {
              if (v) {
                setField("blood_group", v.name);
                setField("blood_group_id", v.id);
              } else {
                setField("blood_group", "");
                setField("blood_group_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Blood Group"
                fullWidth
                error={showErrors && !form.blood_group}
                helperText={
                  showErrors && !form.blood_group
                    ? "Blood Group is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Country */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.countries}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.countries.find((c) => c.name === form.country) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("country", v.name);
                setField("country_id", v.id);
              } else {
                setField("country", "");
                setField("country_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                fullWidth
                error={showErrors && !form.country}
                helperText={
                  showErrors && !form.country ? "Country is required" : ""
                }
              />
            )}
          />
        </Grid>

        {/* State */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.states}
            getOptionLabel={(option) => option?.name || ""}
            // ✅ Use ID for matching, not name
            value={options.states.find((c) => c.name === form.state) || null}
            onChange={(_, v) => {
              if (v) {
                setField("state", v.name); // store name for display
                setField("state_id", v.id); // store id for backend
                // Reset city when state changes
                setField("city", "");
                setField("city_id", null);
              } else {
                setField("state", "");
                setField("state_id", null);
                setField("city", "");
                setField("city_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                fullWidth
                error={showErrors && !form.state}
                helperText={
                  showErrors && !form.state ? "State is required" : ""
                }
              />
            )}
            noOptionsText={options.states.length === 0 ? "No states found" : ""}
          />
        </Grid>

        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.cities}
            getOptionLabel={(option) => option?.name || ""}
            // ✅ Match using city_id or name
            value={
              options.cities.find(
                (c) => c.id == form.city_id || c.name === form.city,
              ) || null
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, v) => {
              if (v) {
                setField("city", v.name); // store name for display
                setField("city_id", v.id); // store id for backend
              } else {
                setField("city", "");
                setField("city_id", null);
              }
            }}
            onOpen={() => {
              if (form.state_id && options.cities.length === 0) {
                fetchFieldOptions("cities", "city", "state", form.state_id);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="City"
                fullWidth
                error={showErrors && !form.city}
                helperText={showErrors && !form.city ? "City is required" : ""}
              />
            )}
            noOptionsText={
              !form.state_id
                ? "Please select a state first"
                : options.cities.length === 0
                  ? "No cities found"
                  : ""
            }
          />
        </Grid>

        {/* Marital Status */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.maritalStatuses}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.maritalStatuses.find(
                (c) => c.name === form.marital_status,
              ) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("marital_status", v.name);
                setField("marital_status_id", v.id);
              } else {
                setField("marital_status", "");
                setField("marital_status_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Marital Status"
                fullWidth
                error={showErrors && !form.marital_status}
                helperText={
                  showErrors && !form.marital_status
                    ? "Marital Status is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Profession */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.profession}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.profession.find((c) => c.name === form.profession) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("profession", v.name);
                setField("profession_id", v.id);
              } else {
                setField("profession", "");
                setField("profession_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Profession"
                fullWidth
                error={showErrors && !form.profession}
                helperText={
                  showErrors && !form.profession ? "Profession is required" : ""
                }
              />
            )}
          />
        </Grid>

        {/* Annual Income */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.annualIncome}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.annualIncome.find((c) => c.name === form.annual_income) ||
              null
            }
            onChange={(_, v) => {
              if (v) {
                setField("annual_income", v.name);
                setField("annual_income_id", v.id);
              } else {
                setField("annual_income", "");
                setField("annual_income_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Annual Income"
                fullWidth
                error={showErrors && !form.annual_income}
                helperText={
                  showErrors && !form.annual_income
                    ? "Annual Income is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Religion */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.religions}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.religions.find((c) => c.name === form.religion) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("religion", v.name);
                setField("religion_id", v.id);
              } else {
                setField("religion", "");
                setField("religion_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Religion"
                fullWidth
                error={showErrors && !form.religion}
                helperText={
                  showErrors && !form.religion ? "Religion is required" : ""
                }
              />
            )}
          />
        </Grid>

        {/* Caste */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.castes}
            getOptionLabel={(option) => option?.name || ""}
            value={options.castes.find((c) => c.name === form.caste) || null}
            onChange={(_, v) => {
              if (v) {
                setField("caste", v.name);
                setField("caste_id", v.id);
              } else {
                setField("caste", "");
                setField("caste_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Caste"
                fullWidth
                error={showErrors && form.religion === "Hindu" && !form.caste}
                helperText={
                  showErrors && form.religion === "Hindu" && !form.caste
                    ? "Caste is required for Hindu"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Zodiac Sign */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.zodiacSigns}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.zodiacSigns.find((c) => c.name === form.zodiac_sign) ||
              null
            }
            onChange={(_, v) => {
              if (v) {
                setField("zodiac_sign", v.name);
                setField("zodiac_sign_id", v.id);
              } else {
                setField("zodiac_sign", "");
                setField("zodiac_sign_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Zodiac Sign"
                fullWidth
                error={
                  showErrors && form.religion === "Hindu" && !form.zodiac_sign
                }
                helperText={
                  showErrors && form.religion === "Hindu" && !form.zodiac_sign
                    ? "Zodiac Sign is required for Hindu"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Zodiac Star */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.zodiacStars}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.zodiacStars.find((c) => c.name === form.zodiac_star) ||
              null
            }
            onChange={(_, v) => {
              if (v) {
                setField("zodiac_star", v.name);
                setField("zodiac_star_id", v.id);
              } else {
                setField("zodiac_star", "");
                setField("zodiac_star_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Zodiac Star"
                fullWidth
                error={
                  showErrors && form.religion === "Hindu" && !form.zodiac_star
                }
                helperText={
                  showErrors && form.religion === "Hindu" && !form.zodiac_star
                    ? "Zodiac Star is required for Hindu"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* About Me */}
        <Grid item xs={12} width={isMobile ? "100%" : "48%"}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="About Me"
            value={form.about_me || ""}
            onChange={(e) => setField("about_me", e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalDetails;
